// ── Free-slot suggestions ──
// Start times for placing a POI into a day without overlapping anything
// already timed on it. Times are wall-clock minutes in the day's display
// zone; every timed item counts as busy, locked or not.

import type { DayView } from "@poi-planner/shared";
import { blockEndTime, blockStartTime } from "./dayViews";
import { zonedInZone } from "./zonedTime";

export interface FreeGap {
  /** Minutes from local midnight, inclusive. */
  start: number;
  /** Minutes from local midnight, exclusive. */
  end: number;
}

export interface FreeSlotOptions {
  /** Earliest suggested start, in minutes from midnight. */
  windowStart?: number;
  /** Latest suggested end, in minutes from midnight. */
  windowEnd?: number;
  /** Suggestions are rounded up to this many minutes. */
  roundTo?: number;
  limit?: number;
}

const MINUTES_PER_DAY = 24 * 60;
export const DEFAULT_FREE_SLOT_WINDOW = { start: 8 * 60, end: 22 * 60 };

/** Wall-clock minutes of an instant on `day`, clamped to that date. */
function wallMinutesOnDay(value: string, day: DayView): number {
  const zoned = zonedInZone(value, day.zone);
  const date = zoned.toPlainDate().toString();
  if (date < day.date) return 0;
  if (date > day.date) return MINUTES_PER_DAY;
  return zoned.hour * 60 + zoned.minute;
}

/** Gaps between a day's timed items inside the waking window. */
export function freeGaps(
  day: DayView,
  windowStart = DEFAULT_FREE_SLOT_WINDOW.start,
  windowEnd = DEFAULT_FREE_SLOT_WINDOW.end,
): FreeGap[] {
  const busy = day.blocks
    .map((block) => ({
      start: wallMinutesOnDay(blockStartTime(block), day),
      end: wallMinutesOnDay(blockEndTime(block), day),
    }))
    .filter((interval) => interval.end > interval.start)
    .sort((a, b) => a.start - b.start);

  const gaps: FreeGap[] = [];
  let cursor = windowStart;
  for (const interval of busy) {
    if (interval.start > cursor) {
      gaps.push({ start: cursor, end: Math.min(interval.start, windowEnd) });
    }
    cursor = Math.max(cursor, interval.end);
    if (cursor >= windowEnd) break;
  }
  if (cursor < windowEnd) gaps.push({ start: cursor, end: windowEnd });
  return gaps.filter((gap) => gap.end > gap.start);
}

/** "HH:mm" for minutes from midnight. */
export function minutesToWallTime(minutes: number): string {
  const hours = Math.floor(minutes / 60);
  return `${String(hours).padStart(2, "0")}:${String(minutes % 60).padStart(2, "0")}`;
}

/**
 * Start times ("HH:mm") at which `durationMinutes` fits a free gap. Each gap
 * offers its start, then later starts spaced so suggestions never overlap one
 * another.
 */
export function suggestStartTimes(
  day: DayView,
  durationMinutes: number,
  {
    windowStart = DEFAULT_FREE_SLOT_WINDOW.start,
    windowEnd = DEFAULT_FREE_SLOT_WINDOW.end,
    roundTo = 15,
    limit = 6,
  }: FreeSlotOptions = {},
): string[] {
  const duration = Math.max(1, durationMinutes);
  const step = Math.max(60, Math.ceil(duration / 30) * 30);
  const suggestions: string[] = [];
  for (const gap of freeGaps(day, windowStart, windowEnd)) {
    let start = Math.ceil(gap.start / roundTo) * roundTo;
    while (start + duration <= gap.end && suggestions.length < limit) {
      suggestions.push(minutesToWallTime(start));
      start += step;
    }
    if (suggestions.length >= limit) break;
  }
  return suggestions;
}
